"use client";
import { useModal } from "@/components/Modal";
import UserAuthModal from "@/components/UserAuthModal";
import { useAPI } from "@/lib/api";
import { ISubscriptionConfig } from "@/types/subscription";
import {
  IUser,
  IUserAdmin,
  IUserPreferences,
  IUserQuota,
  IUserReseller,
  IUserSubscription,
} from "@/types/user";
import { usePathname } from "next/navigation";
import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { useAppContext } from "./AppContext";

type AuthStatus = "loading" | "authenticated" | "unauthorized";

const userContext = () => {
  const { kind } = useAppContext();
  const authModal = useModal();
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [isDark, setIsDark] = useState(false);
  const [isCompat, setIsCompat] = useState(false);
  const pathname = usePathname();
  const {
    t,
    i18n: { language: locale },
  } = useTranslation();
  const api = useAPI(locale);

  const {
    isLoading,
    data: user,
    error,
    mutate: mutateUser,
  } = useSWR(
    () => (kind === "ADMIN" ? "/me" : kind === "RESELLER" ? "/me" : "/user/me"),
    {
      dedupingInterval: 20000,
      revalidateOnFocus: true,
      revalidateOnMount: true,
      refreshInterval: 20000,
      errorRetryInterval: 5000,
    }
  );

  const quota = useSWR(
    status === "authenticated" && kind === "HOME" ? "/user/me/quota" : null,
    {
      dedupingInterval: 20000,
      revalidateOnFocus: false,
      revalidateOnMount: true,
      refreshInterval: 20000,
    }
  );

  const subscription = useMemo(() => {
    const { data } = user || {};
    const expiredAt = data?.user_subscription?.expired_at;
    const status = data?.user_subscription?.status;
    const stillActive = data?.user_subscription?.still_active;

    return {
      ...(data?.user_subscription as IUserSubscription),
      expiredAt,
      stillActive,
      status,
      level: data?.user_subscription?.subscription?.config?.level || 0,
      config: (data?.user_subscription?.subscription?.config ||
        {}) as ISubscriptionConfig,
      slug: data?.user_subscription?.subscription?.slug,
    };
  }, [user]);

  useEffect(() => {
    if (error) {
      if (error.code === 401) {
        if (error.message) {
          switch (error.message) {
            case "cookie token is empty":
              setStatus("unauthorized");
              break;
            case "Token is expired":
              break;
            case "signature is invalid":
              setStatus("unauthorized");
              break;
            case "access_permitted":
              setStatus("unauthorized");
              break;
            default:
              break;
          }
        }
      }
    } else if (user?.data) {
      setStatus("authenticated");
    } else if (!isLoading) {
      setStatus("unauthorized");
    }
  }, [isLoading, user, error]);

  useEffect(() => {
    if (window.localStorage?.theme === "dark") {
      setIsDark(true);
    }

    if (window.localStorage?.menuCompat === "true") {
      setIsCompat(true);
    }
  }, []);

  useEffect(() => {
    if (
      (kind === "HOME" &&
        isDark &&
        pathname.split("/" + locale)[1].startsWith("/dashboard")) ||
      (kind === "ADMIN" && isDark) ||
      kind === "RESELLER"
    ) {
      window.document.documentElement.classList.add("dark");
    } else {
      window.document.documentElement.classList.remove("dark");
    }
  }, [pathname, isDark, locale]);

  const _user = useMemo(() => {
    const _user = { ...(user?.data || {}) } as IUser &
      IUserAdmin &
      IUserReseller;
    _user.user_preferences = _user.user_preferences || ({} as IUserPreferences);
    _user.user_quota = _user.user_quota || ({} as IUserQuota);
    if (quota.data?.data) {
      _user.user_quota = quota.data?.data;
    }

    if (typeof window !== "undefined") {
      const themeColorMeta = document.querySelector('meta[name="theme-color"]');
      if (_user?.user_preferences?.darkmode === true) {
        setIsDark(true);
        localStorage.setItem("theme", "dark");
        if (themeColorMeta) {
          themeColorMeta.setAttribute("content", "#000000");
        }
      } else if (_user?.user_preferences?.darkmode === false) {
        setIsDark(false);
        localStorage.setItem("theme", "light");
        if (themeColorMeta) {
          themeColorMeta.setAttribute("content", "#f9fafb");
        }
      }
    }

    return _user;
  }, [user, quota.data]);

  return {
    user: _user,
    status,
    revalidate: async () => {
      const user = await mutateUser();
      return user;
    },
    refreshQuota: async () => {
      return await quota.mutate();
    },
    isCompat,
    setIsCompat: (value) => {
      localStorage.setItem("menuCompat", value);
      setIsCompat(value);
    },
    isDark,
    subscription,
    setIsDark: (state) => {
      if (kind === "HOME") {
        api.post("/user/preferences", {
          ..._user.user_preferences,
          tz: _user.user_preferences.tz || "GMT+07:00",
          darkmode: state,
        });
      }
      setIsDark(state);
      localStorage.setItem("theme", state ? "dark" : "light");
      if (typeof window !== "undefined") {
        const themeColorMeta = window.document.querySelector(
          'meta[name="theme-color"]'
        );
        if (themeColorMeta) {
          themeColorMeta.setAttribute("content", state ? "#000000" : "#f9fafb");
        }
      }
    },
    authModal,
  };
};

const UserContext = createContext({} as ReturnType<typeof userContext>);

export const useUserContext = () => {
  return useContext(UserContext);
};

export const UserContextProvider = ({ children }) => {
  const value = userContext();
  return (
    <UserContext.Provider value={value}>
      {children}
      <UserAuthModal />
    </UserContext.Provider>
  );
};
